import React, { useState, useRef, useEffect } from "react";
import { Avatar, CircularProgress, Skeleton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import HistoryIcon from "@mui/icons-material/History";
import AddCommentIcon from "@mui/icons-material/AddComment";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ForumIcon from "@mui/icons-material/Forum";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useReactToPrint } from "react-to-print";
import { saveAs } from "file-saver";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import FocusPartsCharts from "./FocusPartsCharts";
import { useReportChatBot, engineResponseToText } from "../../hooks/useReportChatBot";
import AppButton from "../layout/AppButton";
import uiConfig from "../../config/uiConfig";
import { apiService } from "../../services/apiClient";
import { apiUrl } from "../../services/apiUrl";

// Parses GFM-style markdown tables (| a | b |\n| --- | --- |\n| 1 | 2 |) out of a message's text
function extractMarkdownTables(markdown) {
  if (!markdown) return [];
  const lines = markdown.split("\n");
  const isTableRow = (line) => line.includes("|");
  const isSeparatorRow = (line) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line.trim());
  const splitRow = (line) => {
    let trimmed = line.trim();
    if (trimmed.startsWith("|")) trimmed = trimmed.slice(1);
    if (trimmed.endsWith("|")) trimmed = trimmed.slice(0, -1);
    return trimmed.split("|").map((cell) => cell.trim());
  };

  const tables = [];
  let i = 0;
  while (i < lines.length) {
    if (isTableRow(lines[i]) && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      const headers = splitRow(lines[i]);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim() !== "" && isTableRow(lines[i])) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      tables.push({ headers, rows });
    } else {
      i += 1;
    }
  }
  return tables;
}

function csvEscape(value) {
  const str = String(value ?? "");
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

// Friendly "2h ago" / "3d ago" style label for the history list, falling back
// to a locale date string once it's more than a week old
function formatRelativeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function tablesToCsv(tables) {
  return tables
    .map((table, idx) => {
      const lines = tables.length > 1 ? [`Table ${idx + 1}`] : [];
      lines.push(table.headers.map(csvEscape).join(","));
      table.rows.forEach((row) => lines.push(row.map(csvEscape).join(",")));
      return lines.join("\n");
    })
    .join("\n\n");
}

/**
 * Map an engine ChartSpec onto the `graphData` shape FocusPartsCharts renders.
 *
 * Returns null when the spec cannot be plotted (no rows, or no y-field), so the
 * caller renders nothing rather than an empty chart frame.
 */
function toGraphData(chart) {
  if (!chart || !Array.isArray(chart.data) || chart.data.length === 0) return null;
  const yKeys = chart.y_fields || [];
  const xKey = chart.x_field;
  if (!xKey || yKeys.length === 0) return null;

  // FocusPartsCharts renders bar/line/area/pie; anything else degrades to bar,
  // which is always readable even if it is not the ideal encoding.
  const supported = ["bar", "line", "area", "pie"];
  const type = supported.includes(chart.chart_type) ? chart.chart_type : "bar";

  return {
    type,
    title: chart.title || "",
    x_key: xKey,
    y_keys: yKeys,
    data: chart.data,
  };
}

const CONFIDENCE_STYLES = {
  high: { label: "High confidence", color: "#065f46", background: "#d1fae5" },
  medium: { label: "Medium confidence", color: "#92400e", background: "#fef3c7" },
  low: { label: "Low confidence", color: "#991b1b", background: "#fee2e2" },
};

/**
 * Renders the parts of an engine answer that make it trustworthy: key points,
 * confidence, assumptions, limitations, sources and suggested next steps.
 *
 * Each block is omitted when empty, so a simple answer stays simple — but when
 * the engine flags an assumption or a limitation, the user always sees it. That
 * is the difference between an answer and a claim.
 */
function EngineAnswerDetail({ msg }) {
  if (!msg || msg.role !== "assistant" || msg.isWelcome) return null;

  const {
    status,
    keyPoints = [],
    assumptions = [],
    limitations = [],
    citations = [],
    followUps = [],
    confidence,
    requiresHumanReview,
    reviewReason,
  } = msg;

  const hasDetail =
    keyPoints.length ||
    assumptions.length ||
    limitations.length ||
    citations.length ||
    followUps.length ||
    confidence ||
    requiresHumanReview;

  if (!hasDetail) return null;

  const confidenceStyle = confidence ? CONFIDENCE_STYLES[confidence] : null;
  const sectionStyle = { marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" };
  const headingStyle = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#6b7280",
    marginBottom: 6,
  };

  return (
    <div>
      {requiresHumanReview && (
        <div
          style={{
            ...sectionStyle,
            color: "#92400e",
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 6,
            padding: 10,
            fontSize: 12,
          }}
        >
          <strong>Review required before acting.</strong>
          {reviewReason ? ` ${reviewReason}` : ""}
        </div>
      )}

      {keyPoints.length > 0 && (
        <div style={sectionStyle}>
          <div style={headingStyle}>Key points</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55 }}>
            {keyPoints.map((point, idx) => (
              <li key={idx}>{point}</li>
            ))}
          </ul>
        </div>
      )}

      {(confidence || assumptions.length > 0) && (
        <div style={sectionStyle}>
          {confidenceStyle && (
            <span
              style={{
                display: "inline-block",
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 999,
                color: confidenceStyle.color,
                background: confidenceStyle.background,
                marginBottom: assumptions.length ? 8 : 0,
              }}
            >
              {confidenceStyle.label}
            </span>
          )}
          {assumptions.length > 0 && (
            <>
              <div style={{ ...headingStyle, marginTop: 8 }}>Assumptions</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#4b5563" }}>
                {assumptions.map((a, idx) => (
                  <li key={idx}>{a.statement}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {limitations.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ ...headingStyle, color: "#b45309" }}>Limitations</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#92400e" }}>
            {limitations.map((l, idx) => (
              <li key={idx}>{l.statement}</li>
            ))}
          </ul>
        </div>
      )}

      {citations.length > 0 && (
        <details style={sectionStyle}>
          <summary style={{ ...headingStyle, cursor: "pointer", marginBottom: 0 }}>
            Sources ({citations.length})
          </summary>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 11, color: "#6b7280" }}>
            {citations.map((c, idx) => (
              <li key={c.evidence_id || idx}>
                {c.label} <span style={{ opacity: 0.75 }}>— {c.source}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {followUps.length > 0 && status === "answered" && (
        <div style={sectionStyle}>
          <div style={headingStyle}>Suggested next steps</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.55 }}>
            {followUps.map((f, idx) => (
              <li key={idx}>
                {f.text}
                {f.kind === "action" && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 10,
                      padding: "1px 5px",
                      borderRadius: 4,
                      background: "#eef2ff",
                      color: "#4338ca",
                    }}
                  >
                    action
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Props:
 *   reportName  — display name of the current report
 *   pageName    — current page/tab name (for context)
 *   reportId    — id of the current report (used to scope saved conversations)
 *   groupId     — id of the current user group (used to scope saved conversations)
 *   getVisuals  — async function that returns [{title, type, csv}] from the embedded report
 *   onClose     — called when the user closes the panel (parent controls mount/unmount)
 */
export default function ReportChatBot({ reportName, pageName, reportId, reportGuid, groupId, getVisuals, onClose }) {
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);

  useEffect(() => {
    if (!reportGuid) return;
    apiService.get(apiUrl.onboardingChatStatus(reportGuid))
      .then((res) => {
        const d = res?.data ?? res;
        setOnboardingStatus(d);
        if (d?.enabled && d?.suggestedQuestions?.length) {
          setSuggestedQuestions(d.suggestedQuestions);
        }
      })
      .catch(() => setOnboardingStatus({ enabled: false }));
  }, [reportGuid]);

  const welcomeMessage = () => ({
    role: "assistant",
    text: `Hi! I'm your data assistant for **${reportName || "this report"}**. Ask me anything about the data currently visible.`,
    isWelcome: true,
  });

  const [messages, setMessages]   = useState([welcomeMessage()]);
  const [input, setInput]         = useState("");
  // Tool Data (cached visual exports, default) vs Source Data (a live, RLS-scoped
  // DAX query against the report's own dataset) vs Text-to-DAX (onboarding-grounded).
  // Auto-selects "onboarding" when the dashboard has been onboarded.
  const [dataMode, setDataMode] = useState("tool");

  useEffect(() => {
    if (onboardingStatus?.enabled) setDataMode("onboarding");
  }, [onboardingStatus]);
  const {
    streamReportQuestion,
    askSourceDataQuestion,
    listChatSessions,
    getChatSessionMessages,
    loading,
  } = useReportChatBot();
  const bottomRef                 = useRef(null);
  const panelRef                  = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  // Conversation history — current session id (null until the first message is sent
  // or a past conversation is resumed), plus the list shown in the History panel
  const [sessionId, setSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [resumingSession, setResumingSession] = useState(false);
  const [sessions, setSessions] = useState([]);

  const loadSessions = async () => {
    setHistoryLoading(true);
    try {
      const list = await listChatSessions({ reportId, groupId });
      setSessions(list);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) loadSessions();
  };

  const startNewConversation = () => {
    setSessionId(null);
    setMessages([welcomeMessage()]);
    setShowHistory(false);
  };

  const resumeSession = async (id) => {
    setShowHistory(false);
    setResumingSession(true);
    try {
      const saved = await getChatSessionMessages(id);
      setSessionId(id);
      setMessages(
        saved.length > 0
          ? saved.map((m) => {
              // Rebuild a charts array from the saved graphData so the chart
              // rendering code (which reads msg.charts) works for history too.
              const charts = [];
              if (m.graphData && m.graphData.x_key && (m.graphData.y_keys || []).length > 0) {
                charts.push({
                  chart_type: m.graphData.type,
                  x_field: m.graphData.x_key,
                  y_fields: m.graphData.y_keys,
                  data: m.graphData.data,
                  title: m.graphData.title,
                });
              }
              return {
                role: m.role,
                text: m.text,
                reasoning: m.reasoning,
                chartData: m.chartData,
                graphData: m.graphData,
                charts,
              };
            })
          : [welcomeMessage()]
      );
    } finally {
      setResumingSession(false);
    }
  };

  // Stable per-panel conversation id. The engine uses it for session memory
  // (reference resolution only — never as evidence) and to key its answer cache.
  const conversationIdRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `conv-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
  const conversationId = conversationIdRef.current;

  // Refs to each assistant message's rendered content, so a single print hook
  // can be pointed at whichever message's "Download PDF" button was clicked
  const messageContentRefs = useRef({});
  const printTargetRef = useRef(null);
  const printMessage = useReactToPrint({
    content: () => printTargetRef.current,
    documentTitle: `${reportName || "report"}-ai-response`,
  });

  const handleDownloadPdf = (index) => {
    printTargetRef.current = messageContentRefs.current[index];
    printMessage();
  };

  const handleDownloadCsv = (msg) => {
    const tables = extractMarkdownTables(msg.text);
    if (tables.length === 0) return;
    const csv = tablesToCsv(tables);
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), `report-chat-data-${Date.now()}.csv`);
  };

  // Live progress while a question is in flight — driven by SSE events from the backend
  const [stageMessage, setStageMessage] = useState("Thinking…");
  const [liveLog, setLiveLog] = useState([]);
  // Kept visible while the stream runs so a hung request cannot look like a silent UI.
  // Surfaced in the loading bubble when a stream fails, so a broken request
  // reports itself in the panel rather than only in DevTools.
  const [debug, setDebug] = useState({ error: null });

  useEffect(() => {
    // "nearest" (not the default "start") keeps this scroll confined to the
    // messages pane itself — with the default, if that pane were ever not
    // truly clipping its content (e.g. a transient layout reflow), the browser
    // walks up to the next scrollable ancestor instead and scrolls *that*,
    // which is exactly what made the whole panel/page appear to move.
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  useEffect(() => {
    const syncFullscreenState = () => {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fullscreenElement === panelRef.current);
    };

    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("webkitfullscreenchange", syncFullscreenState);

    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener(
        "webkitfullscreenchange",
        syncFullscreenState
      );
    };
  }, []);

  /**
   * Send a question, or re-send an earlier one.
   *
   * `refresh` is what the "Ask again" button sets: the engine caches answers, so
   * an identical question returns the previous one in a few milliseconds. That is
   * usually what you want and occasionally not — after a data refresh, or when
   * the answer looked wrong — and there was no way to say so.
   */
  /**
   * "Source Data" mode: a live, RLS-scoped DAX query against the report's own
   * Power BI dataset rather than the cached visual exports "Tool Data" uses.
   * Not streamed — the backend does two LLM calls (write the DAX, then compose
   * the answer from its rows) around a single Power BI round trip.
   */
  const sendSourceDataMessage = async (q, askAgain) => {
    setStageMessage("Querying source data…");
    setLiveLog([]);
    const startedAt = performance.now();

    try {
      // The dataset's real schema isn't queryable via Power BI's executeQueries
      // API, so the exported visuals' columns are the grounding hint instead.
      let visuals = [];
      if (typeof getVisuals === "function") {
        try {
          visuals = await getVisuals();
        } catch (e) {
          console.warn("Could not extract visual data:", e);
        }
      }

      const result = await askSourceDataQuestion({ question: q, reportId, reportName, visuals });
      const elapsedMs = Math.round(performance.now() - startedAt);
      const text = result?.answer || "Sorry, I couldn't get a response.";

      setMessages((m) => {
        const newMsg = {
          role: "assistant",
          text,
          reasoning: [],
          status: result?.success ? "answered" : null,
          elapsedMs,
          servedFromCache: false,
          question: q,
          keyPoints: [],
          citations: [],
          assumptions: [],
          limitations: [],
          followUps: [],
          confidence: null,
          requiresHumanReview: false,
          reviewReason: null,
          table: null,
          charts: [],
          clarification: null,
          refusal: null,
          trace: null,
          // Marks this bubble as answered from a live dataset query, so the UI
          // can show the DAX + RLS role it ran under instead of engine reasoning.
          sourceData: true,
          dax: result?.dax || null,
          rlsRoles: result?.rlsRoles || [],
          rowCount: typeof result?.rowCount === "number" ? result.rowCount : null,
        };
        if (askAgain?.replaceIndex != null) {
          return m.map((item, idx) => (idx === askAgain.replaceIndex ? newMsg : item));
        }
        return [...m, newMsg];
      });
    } catch (err) {
      const detail = err?.message || String(err);
      console.error("[ReportChatBot] source-data query failed:", err);
      setDebug((d) => ({ ...d, error: detail }));
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Something went wrong querying the source dataset.\n\n**Details:** " + detail,
        },
      ]);
    } finally {
      setLiveLog([]);
    }
  };

  const sendOnboardingMessage = async (q, askAgain) => {
    setStageMessage("Querying via Text-to-DAX…");
    setLiveLog([]);
    const startedAt = performance.now();
    try {
      const res = await apiService.post(apiUrl.onboardingChat(), { reportGuid, question: q });
      const d = res?.data ?? res;
      const elapsedMs = Math.round(performance.now() - startedAt);
      const gs = d?.groundingStats || {};
      const reasoning = [
        { key: "grounding:tables", label: `Semantic model: ${gs.semanticTables || 0} tables, ${gs.semanticColumns || 0} columns`, status: "done", type: "stage" },
        { key: "grounding:measures", label: `Measures loaded: ${gs.measures || 0}`, status: "done", type: "stage" },
        { key: "grounding:exemplars", label: `Q&A exemplars used: ${gs.exemplars || 0}`, status: "done", type: "stage" },
        { key: "grounding:kpis", label: `KPIs grounded: ${gs.kpis || 0}`, status: "done", type: "stage" },
        { key: "grounding:metadata", label: `Metadata / glossary terms: ${gs.metadata || 0}`, status: "done", type: "stage" },
        { key: "dax", label: d?.dax ? "DAX query generated" : "DAX generation attempted", status: d?.dax ? "done" : "failed", type: "node" },
        { key: "execute", label: typeof d?.rowCount === "number" ? `Query returned ${d.rowCount} row${d.rowCount === 1 ? "" : "s"}` : "Query executed", status: d?.success ? "done" : "failed", type: "node" },
      ];
      const newMsg = {
        role: "assistant",
        text: d?.answer || "Sorry, I couldn't get a response.",
        reasoning,
        status: d?.success ? "answered" : null,
        elapsedMs,
        servedFromCache: false,
        question: q,
        sourceData: true,
        dax: d?.dax || null,
        rowCount: typeof d?.rowCount === "number" ? d.rowCount : null,
        keyPoints: [], citations: [], assumptions: [], limitations: [], followUps: [],
        confidence: null, requiresHumanReview: false, reviewReason: null,
        table: null, charts: [], clarification: null, refusal: null, trace: null,
      };
      setMessages((m) => {
        if (askAgain?.replaceIndex != null) return m.map((item, idx) => idx === askAgain.replaceIndex ? newMsg : item);
        return [...m, newMsg];
      });
    } catch (err) {
      const detail = err?.message || String(err);
      setMessages((m) => [...m, { role: "assistant", text: "Something went wrong.\n\n**Details:** " + detail }]);
    } finally {
      setLiveLog([]);
    }
  };

  const sendMessage = async (askAgain = null) => {
    const q = (askAgain?.question ?? input).trim();
    if (!q || loading) return;
    const refresh = askAgain?.refresh === true;

    if (!askAgain) setInput("");
    if (!askAgain) setMessages((m) => [...m, { role: "user", text: q }]);
    // Clear the old answer immediately so stale content never shows alongside the new stream
    if (askAgain?.replaceIndex != null) {
      setMessages((m) => m.map((item, idx) =>
        idx === askAgain.replaceIndex ? { ...item, text: "", reasoning: [], charts: [], _refreshing: true } : item
      ));
    }
    setDebug({ error: null });

    if (dataMode === "source") {
      await sendSourceDataMessage(q, askAgain);
      return;
    }

    if (dataMode === "onboarding") {
      await sendOnboardingMessage(q, askAgain);
      return;
    }

    setStageMessage("Thinking…");
    setLiveLog([]);

    // One row per step, keyed, so a step that reports itself twice (started then
    // completed) updates in place instead of appearing twice. Held in a Map
    // rather than in state so the final list is complete the moment the stream
    // ends, without waiting on React's async updates.
    const progress = new Map();
    // Wall clock, because it is what the user actually waited. Server stage
    // durations sum to less than this and would understate the experience.
    const startedAt = performance.now();

    try {
      // Extract data from the embedded report visuals
      let visuals = [];
      if (typeof getVisuals === "function") {
        try {
          visuals = await getVisuals();
        } catch (e) {
          console.warn("Could not extract visual data:", e);
        }
      }

      const result = await streamReportQuestion(
        { question: q, reportName, pageName, visuals, conversationId, refresh, sessionId, reportId, groupId },
        {
          // The engine's own progress channel: already labelled for a reader,
          // one row per step. The raw `stage`/`node` events are deliberately
          // not consumed here — they fire three times per stage and mostly
          // carry no label, which is what produced duplicate and blank rows.
          onProgress: (data) => {
            if (!data?.key || !data?.label) return;
            progress.set(data.key, {
              type: data.group === "execute" ? "node" : "stage",
              key: data.key,
              label: data.label,
              detail: data.detail || "",
              status: data.status,
              percent: data.percent,
            });
            // The headline tells the user what is happening *now*, so only a
            // step still running should claim it.
            if (data.status === "running") setStageMessage(data.label + "…");
            setLiveLog([...progress.values()]);
          },
        }
      );

      const text = engineResponseToText(result);
      const answer = result?.answer || null;

      // The backend creates the saved-conversation row on the first turn and
      // reports its id back here — without capturing it, every later turn in
      // the same conversation looked like a fresh one and was saved separately
      // (or not at all, since "session save is not working" was this exact bug).
      if (result?.sessionId && result.sessionId !== sessionId) {
        setSessionId(result.sessionId);
      }

      // Insertion-ordered, so the retained list reads in the order the work
      // actually happened rather than stages-then-skills.
      const finalReasoning = [...progress.values()];
      const elapsedMs = Math.round(performance.now() - startedAt);
      // The engine says so itself rather than the UI inferring it from a
      // suspiciously fast reply.
      const cacheRow = progress.get("stage:cache");
      const servedFromCache = /reusing/i.test(cacheRow?.label || "");

      setMessages((m) => {
        const newMsg = {
          role: "assistant",
          text,
          reasoning: finalReasoning,
          status: result?.status || null,
          elapsedMs,
          servedFromCache,
          question: q,
          keyPoints: answer?.keyPoints || [],
          citations: answer?.citations || [],
          assumptions: answer?.assumptions || [],
          limitations: answer?.limitations || [],
          followUps: answer?.followUps || [],
          confidence: answer?.confidence || null,
          requiresHumanReview: answer?.requiresHumanReview || false,
          reviewReason: answer?.reviewReason || null,
          table: answer?.table || null,
          charts: answer?.charts || [],
          clarification: result?.clarification || null,
          refusal: result?.refusal || null,
          trace: result?.trace || null,
        };
        // Replace the specific message when retrying, otherwise append
        if (askAgain?.replaceIndex != null) {
          return m.map((item, idx) => idx === askAgain.replaceIndex ? newMsg : item);
        }
        return [...m, newMsg];
      });
    } catch (err) {
      const detail = err?.message || String(err);
      console.error("[ReportChatBot] stream failed:", err);
      setDebug((d) => ({ ...d, error: detail }));
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "Something went wrong contacting the engine.\n\n**Details:** " +
            detail +
            "\n\nOpen the DevTools console for the full stack, or try " +
            "[`/engine-stream-test.html`](/engine-stream-test.html) to test the raw stream.",
        },
      ]);
    } finally {
      setLiveLog([]);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleFullscreen = async () => {
    if (!panelRef.current) return;

    try {
      if (!isFullscreen) {
        if (panelRef.current.requestFullscreen) {
          await panelRef.current.requestFullscreen();
        } else if (panelRef.current.webkitRequestFullscreen) {
          panelRef.current.webkitRequestFullscreen();
        }
        return;
      }

      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    } catch (e) {
      console.warn("Fullscreen toggle failed:", e);
    }
  };

  const closeChat = async () => {
    if (isFullscreen) {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      } catch (e) {
        console.warn("Could not exit fullscreen on close:", e);
      }
    }
    onClose?.();
  };

  const assistantGradient =
    "var(--app-ai-assistant-gradient, linear-gradient(135deg,#253978,#1a2a5e))";
  const assistantPrimary = "var(--app-ai-assistant-primary, #253978)";

  // Shared ReactMarkdown renderers — used for both the final answer and the
  // per-batch "analysis steps" findings, so both render actual markdown (not raw text)
  const markdownComponents = {
    h1: ({ node, children, ...props }) => (
      <h2
        {...props}
        style={{
          fontSize: "1.05rem",
          fontWeight: 700,
          margin: "0 0 8px",
          paddingBottom: 5,
          color: assistantPrimary,
          borderBottom: "2px solid #eef0f7",
        }}
      >
        {children}
      </h2>
    ),
    h2: ({ node, children, ...props }) => (
      <h3
        {...props}
        style={{ fontSize: "0.98rem", fontWeight: 700, margin: "16px 0 10px", color: assistantPrimary }}
      >
        {children}
      </h3>
    ),
    h3: ({ node, children, ...props }) => (
      <h4
        {...props}
        style={{ fontSize: "0.9rem", fontWeight: 700, margin: "8px 0 4px", color: "#1f2937" }}
      >
        {children}
      </h4>
    ),
    h4: ({ node, children, ...props }) => (
      <h5
        {...props}
        style={{ fontSize: "0.85rem", fontWeight: 600, margin: "6px 0 4px", color: "#374151" }}
      >
        {children}
      </h5>
    ),
    p: ({ node, ...props }) => (
      <p {...props} style={{ margin: "0 0 6px 0" }} />
    ),
    strong: ({ node, ...props }) => (
      <strong {...props} style={{ fontWeight: 700, color: "#111827" }} />
    ),
    em: ({ node, ...props }) => (
      <em {...props} style={{ color: "#4b5563" }} />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote
        {...props}
        style={{
          margin: "6px 0",
          padding: "6px 12px",
          borderLeft: `3px solid ${assistantPrimary}`,
          background: "#f6f5ff",
          color: "#4b5563",
          fontStyle: "italic",
          borderRadius: "0 10px 10px 0",
        }}
      />
    ),
    hr: ({ node, ...props }) => (
      <hr
        {...props}
        style={{
          border: "none",
          height: 1,
          background: "linear-gradient(90deg, transparent, #e5e7eb, transparent)",
          margin: "8px 0",
        }}
      />
    ),
    ul: ({ node, ...props }) => (
      <ul {...props} style={{ margin: "0 0 6px 4px", paddingLeft: 16, listStyleType: "disc" }} />
    ),
    ol: ({ node, ...props }) => (
      <ol {...props} style={{ margin: "0 0 6px 4px", paddingLeft: 18 }} />
    ),
    li: ({ node, ...props }) => (
      <li {...props} style={{ marginBottom: 2, paddingLeft: 2 }} />
    ),
    table: ({ node, ...props }) => (
      <div
        style={{
          overflowX: "auto",
          margin: "8px 0",
          borderRadius: 10,
          border: "1px solid #eef0f7",
        }}
      >
        <table
          {...props}
          className="w-full text-xs modern-table"
          style={{ borderCollapse: "collapse" }}
        />
      </div>
    ),
    pre: ({ node, ...props }) => (
      <pre
        {...props}
        style={{
          margin: "8px 0",
          padding: "12px 14px",
          background: "#1e293b",
          color: "#e2e8f0",
          borderRadius: "10px",
          overflowX: "auto",
          fontSize: "12px",
          lineHeight: 1.6,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        }}
      />
    ),
    code: ({ inline, node, ...props }) =>
      inline ? (
        <code
          {...props}
          style={{
            background: "#f0edff",
            color: assistantPrimary,
            borderRadius: "5px",
            padding: "2px 6px",
            fontSize: "12px",
            fontWeight: 600,
          }}
        />
      ) : (
        <code {...props} style={{ background: "transparent", color: "inherit" }} />
      ),
    a: ({ node, children, ...props }) => (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: assistantPrimary, fontWeight: 600, textDecoration: "underline" }}
      >
        {children}
      </a>
    ),
  };

  return (
    // Absolutely positioned against the column PowerBiReports gives it, rather
    // than sized via width/height:100% — a percentage/flex chain that grows past
    // its box (as this one repeatedly did) drags this panel's own header along
    // with it; an absolutely positioned box can't be pushed around like that.
    <div style={{ position: "absolute", inset: 0 }}>
      <div
        ref={panelRef}
        className="bg-white overflow-hidden"
        style={{
          width: isFullscreen ? "100vw" : "100%",
          height: isFullscreen ? "100vh" : "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxShadow: isFullscreen ? "0 20px 60px rgba(0,0,0,0.2)" : "none",
        }}
      >
          {/* header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ background: assistantGradient }}
          >
            <div className="flex items-center gap-2">
              {showHistory && (
                <button
                  onClick={() => setShowHistory(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors -ml-1"
                  title="Back to conversation"
                >
                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                </button>
              )}
              <div
                className="flex items-center justify-center w-7 h-7 rounded-full"
                style={{ background: "rgba(255,255,255,0.15)" }}
              >
                {showHistory ? (
                  <ForumIcon sx={{ fontSize: 15, color: "white" }} />
                ) : (
                  <SmartToyIcon sx={{ fontSize: 16, color: "white" }} />
                )}
              </div>
              <div>
                <span className="text-white text-sm font-bold leading-tight">
                  {showHistory ? "Conversation History" : "AI Data Assistant"}
                </span>
                {!showHistory && (
                  <p className="text-white/90 text-[10px] leading-tight truncate max-w-[180px]">
                    {reportName || "Report"}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!showHistory && (
                <>
                  <button
                    onClick={startNewConversation}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    title="New conversation"
                  >
                    <AddCommentIcon sx={{ fontSize: 16 }} />
                  </button>
                  <button
                    onClick={toggleHistory}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    title="Conversation history"
                  >
                    <HistoryIcon sx={{ fontSize: 16 }} />
                  </button>
                  <button
                    onClick={toggleFullscreen}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    title={isFullscreen ? "Exit full screen" : "Full screen"}
                  >
                    {isFullscreen ? (
                      <FullscreenExitIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <FullscreenIcon sx={{ fontSize: 16 }} />
                    )}
                  </button>
                </>
              )}
              {showHistory && (
                <button
                  onClick={startNewConversation}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  title="Start a new conversation"
                >
                  <AddCommentIcon sx={{ fontSize: 16 }} />
                </button>
              )}
              <button
                onClick={closeChat}
                className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Close"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* history panel — replaces the messages+input area while open, so it never overlaps the main header */}
          {showHistory && (
            <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50">
              <div className="p-3 space-y-2">
                {historyLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-3 rounded-2xl bg-white border border-gray-100">
                      <Skeleton variant="circular" width={36} height={36} />
                      <div className="flex-1">
                        <Skeleton variant="text" width="70%" height={16} />
                        <Skeleton variant="text" width="40%" height={12} />
                      </div>
                    </div>
                  ))
                ) : sessions.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-10 text-center">
                    <div
                      className="flex items-center justify-center w-12 h-12 rounded-full"
                      style={{ background: "#f0edff" }}
                    >
                      <ChatBubbleOutlineIcon sx={{ fontSize: 22, color: assistantPrimary }} />
                    </div>
                    <p className="text-xs text-gray-400 max-w-[220px]">
                      No saved conversations yet. Ask a question to start one!
                    </p>
                  </div>
                ) : (
                  sessions.map((s) => {
                    const isActive = s.sessionId === sessionId;
                    return (
                      <button
                        key={s.sessionId}
                        onClick={() => resumeSession(s.sessionId)}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-left bg-white border transition-all hover:shadow-md"
                        style={{
                          borderColor: isActive ? assistantPrimary : "#f1f5f9",
                          borderLeftWidth: isActive ? 4 : 1,
                          background: isActive ? "#f6f5ff" : "white",
                        }}
                      >
                        <div
                          className="flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0"
                          style={{
                            background: isActive ? assistantGradient : "#f0edff",
                          }}
                        >
                          <ChatBubbleOutlineIcon
                            sx={{ fontSize: 17, color: isActive ? "white" : assistantPrimary }}
                          />
                        </div>
                        <div className="grid gap-1 flex-1">
                          <span className="text-sm font-semibold text-gray-800 truncate">{s.title}</span>
                          <span className="text-[10px] text-gray-400 mt-0.5">
                            {formatRelativeTime(s.updatedAt)}
                          </span>
                        </div>
                        <ChevronRightIcon sx={{ fontSize: 18, color: "#cbd5e1", flexShrink: 0 }} />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {!showHistory && (
          <>
          {resumingSession && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 20,
              background: "rgba(0,0,0,0.7)",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
            }}>
              <CircularProgress size={32} thickness={4} sx={{ color: "white" }} />
              <span style={{ color: "white", fontSize: 13, fontWeight: 500 }}>Loading conversation…</span>
            </div>
          )}
          {/* messages */}
          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
            {messages.map((msg, i) => {
              const messageTables = msg.role === "assistant" ? extractMarkdownTables(msg.text) : [];
              return (
              <div
                key={i}
                className={`flex items-end gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
              >
                {msg.role === "assistant" && (
                  <Avatar
                    sx={{ width: 26, height: 26, bgcolor: assistantPrimary, fontSize: 10 }}
                  >
                    <span className="text-[12px]">AI</span>
                  </Avatar>
                )}
                <div
                  className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "text-white rounded-br-sm whitespace-pre-wrap"
                      : "bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100"
                  }`}
                  style={msg.role === "user" ? { background: assistantPrimary } : undefined}
                >
                  {msg.role === "assistant" ? (
                    msg._refreshing ? (
                      <div className="py-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CircularProgress size={14} thickness={4} sx={{ color: assistantPrimary }} />
                          <span className="text-xs text-gray-500">{stageMessage}</span>
                        </div>
                        {liveLog.length > 0 && (
                          <details className="mt-2 border-t border-gray-100 pt-2" open>
                            <summary className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden mb-1.5">
                              <AutoAwesomeIcon sx={{ fontSize: 13, color: assistantPrimary }} />
                              Analysis steps
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#f0edff", color: assistantPrimary }}>
                                {liveLog.length}
                              </span>
                              <ExpandMoreIcon sx={{ fontSize: 14, color: "#9ca3af", marginLeft: "auto" }} />
                            </summary>
                            <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                              {liveLog.map((r) => (
                                <div key={r.key} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                  {r.status === "running" ? (
                                    <CircularProgress size={10} sx={{ color: assistantPrimary }} />
                                  ) : r.status === "failed" ? (
                                    <ErrorOutlineIcon sx={{ fontSize: 13, color: "#ef4444" }} />
                                  ) : (
                                    <CheckCircleIcon sx={{ fontSize: 13, color: "#22c55e" }} />
                                  )}
                                  <span className={r.status === "running" ? "text-gray-700" : ""}>{r.label}</span>
                                  {r.detail && <span className="text-gray-400 truncate">· {r.detail}</span>}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ) : (
                    <div className="markdownMessage" ref={(el) => (messageContentRefs.current[i] = el)}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {msg.text || ""}
                      </ReactMarkdown>

                      {msg.isWelcome && suggestedQuestions.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>
                            Suggested questions
                          </span>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {suggestedQuestions.map((q, qi) => (
                              <button
                                key={qi}
                                onClick={() => { setDataMode("onboarding"); setInput(q); }}
                                style={{
                                  textAlign: "left", fontSize: 11.5, color: assistantPrimary,
                                  background: "#f0edff", border: "1px solid #e0d9ff", borderRadius: 16,
                                  padding: "5px 12px", cursor: "pointer", fontWeight: 500,
                                  transition: "all .15s", lineHeight: 1.3,
                                }}
                                onMouseEnter={(e) => { e.target.style.background = "#e0d9ff"; }}
                                onMouseLeave={(e) => { e.target.style.background = "#f0edff"; }}
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Typed chart specs from the engine. These plot the same
                          evidence the narrative cites — unlike the previous
                          implementation, no value here was parsed out of model
                          prose, so the chart and the text can never disagree. */}
                      {(msg.charts || []).map((chart, chartIdx) => {
                        const graphData = toGraphData(chart);
                        if (!graphData) return null;
                        return (
                          <div
                            key={chartIdx}
                            style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}
                          >
                            <div className="text-xs font-semibold text-gray-600 mb-3">
                              {chart.title || "Visualisation"}
                            </div>
                            <div
                              className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 overflow-hidden"
                              style={{ minHeight: "450px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                            >
                              <FocusPartsCharts
                                chartType={graphData.type}
                                data={graphData}
                                compact={true}
                              />
                            </div>
                          </div>
                        );
                      })}

                      {/* Engine-grounded detail. Rendered after the narrative
                          because a reader wants the conclusion first, but always
                          rendered — an answer without its basis is exactly what
                          this integration replaced. */}
                      <EngineAnswerDetail msg={msg} />
                    </div>
                    )
                  ) : (
                    msg.text
                  )}

                  {msg.role === "assistant" && msg.text && !msg.isWelcome && !msg._refreshing && (
                    <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100">
                      {messageTables.length > 0 && (
                        <button
                          onClick={() => handleDownloadCsv(msg)}
                          className="flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 px-1.5 py-1 rounded-md hover:bg-gray-100 transition-colors"
                          title="Download the table data as CSV"
                        >
                          <TableChartIcon sx={{ fontSize: 13 }} />
                          Export Data in CSV
                        </button>
                      )}
                    </div>
                  )}

                  {/* How long it took, whether it was recomputed, and a way to
                      ask again. Shown together because they answer one question:
                      "can I trust that this is current?" A 4 ms reply is
                      reassuring only once you know it came from cache. */}
                  {msg.role === "assistant" && !msg.isWelcome && !msg._refreshing &&
                    typeof msg.elapsedMs === "number" && (
                    <div className="flex items-center gap-2 mt-2 text-[10.5px] text-gray-400">
                      <AccessTimeIcon sx={{ fontSize: 12 }} />
                      <span>
                        {msg.elapsedMs < 1000
                          ? `${msg.elapsedMs} ms`
                          : `${(msg.elapsedMs / 1000).toFixed(1)} s`}
                      </span>
                      {msg.servedFromCache && (
                        <span
                          className="px-1.5 py-0.5 rounded-full font-medium"
                          style={{ background: "#fef3c7", color: "#92400e" }}
                          title="An identical question was already answered for this data. Ask again to recompute."
                        >
                          from cache
                        </span>
                      )}
                      {msg.sourceData && (
                        <span
                          className="px-1.5 py-0.5 rounded-full font-medium"
                          style={{ background: "#e0f2fe", color: "#075985" }}
                          title={
                            msg.rlsRoles?.length
                              ? `Answered from a live query against the source dataset, under RLS role(s): ${msg.rlsRoles.join(", ")}`
                              : "Answered from a live query against the source dataset."
                          }
                        >
                          source data
                        </span>
                      )}
                      {msg.question && (
                        <button
                          type="button"
                          onClick={() => sendMessage({ question: msg.question, refresh: true, replaceIndex: i })}
                          disabled={loading}
                          className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-40 transition-colors"
                          style={{ color: assistantPrimary }}
                          title="Recompute from the current data, ignoring the cached answer"
                        >
                          <RefreshIcon sx={{ fontSize: 12 }} />
                          Ask again
                        </button>
                      )}
                    </div>
                  )}

                  {msg.role === "assistant" && msg.sourceData && msg.dax && (
                    <details className="group mt-3 rounded-xl border border-gray-100 bg-gray-50/70 overflow-hidden">
                      <summary className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer select-none list-none hover:bg-gray-100 transition-colors [&::-webkit-details-marker]:hidden">
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                          <AutoAwesomeIcon sx={{ fontSize: 14, color: assistantPrimary }} />
                          View DAX query
                          {typeof msg.rowCount === "number" && (
                            <span
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                              style={{ background: "#f0edff", color: assistantPrimary }}
                            >
                              {msg.rowCount} row{msg.rowCount === 1 ? "" : "s"}
                            </span>
                          )}
                        </span>
                        <ExpandMoreIcon sx={{ fontSize: 18, color: "#9ca3af" }} className="transition-transform duration-200 group-open:rotate-180" />
                      </summary>
                      <pre className="px-3 py-2.5 m-0 text-[11px] leading-relaxed text-gray-700 border-t border-gray-100 bg-white overflow-x-auto whitespace-pre-wrap">
                        {msg.dax}
                      </pre>
                    </details>
                  )}

                  {msg.role === "assistant" && msg.reasoning?.length > 0 && (
                    <details className="group mt-3 rounded-xl border border-gray-100 bg-gray-50/70 overflow-hidden">
                      <summary
                        className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer select-none list-none hover:bg-gray-100 transition-colors [&::-webkit-details-marker]:hidden"
                      >
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                          <AutoAwesomeIcon sx={{ fontSize: 14, color: assistantPrimary }} />
                          View analysis steps
                          <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                            style={{ background: "#f0edff", color: assistantPrimary }}
                          >
                            {msg.reasoning.length}
                          </span>
                        </span>
                        <ExpandMoreIcon
                          sx={{ fontSize: 18, color: "#9ca3af" }}
                          className="transition-transform duration-200 group-open:rotate-180"
                        />
                      </summary>
                      <div className="px-3 py-2.5 space-y-2 max-h-48 overflow-y-auto border-t border-gray-100 bg-white">
                        {msg.reasoning.map((r) => (
                          <div
                            key={r.key}
                            className="flex items-start gap-2 rounded-lg bg-gray-50 border border-gray-100 px-2.5 py-2"
                          >
                            {r.status === "failed" ? (
                              <ErrorOutlineIcon sx={{ fontSize: 14, color: "#ef4444", mt: "1px" }} />
                            ) : (
                              <CheckCircleIcon sx={{ fontSize: 14, color: "#22c55e", mt: "1px" }} />
                            )}
                            <div className="min-w-0">
                              <div className="text-[11.5px] text-gray-700 leading-relaxed">
                                {r.label}
                              </div>
                              {/* Only shown when the engine reported something
                                  concrete; an empty line reads as a failure. */}
                              {r.detail && (
                                <div className="text-[10.5px] text-gray-400 leading-relaxed">
                                  {r.detail}
                                </div>
                              )}
                            </div>
                            {r.type === "node" && (
                              <span
                                className="ml-auto flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                                style={{ background: "#f0edff", color: assistantPrimary }}
                              >
                                skill
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
              );
            })}

            {loading && !messages.some(m => m._refreshing) && (
              <div className="flex items-end gap-2">
                <Avatar sx={{ width: 26, height: 26, bgcolor: assistantPrimary, fontSize: 11 }}>
                  AI
                </Avatar>
                <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-bl-sm px-4 py-3 max-w-[82%]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{stageMessage}</span>
                    <div className="flex gap-1.5">
                      {[0, 1, 2].map((d) => (
                        <span
                          key={d}
                          className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                          style={{ animationDelay: `${d * 0.15}s` }}
                        />
                      ))}
                    </div>
                  </div>
                  {/* Only shown when something actually broke. The frame
                      counters this replaced were invaluable while hunting a
                      silent stream, but a live "events: 41 progress · 43 stage"
                      readout is developer telemetry sitting in front of a
                      business audience — and the labelled rows below now say
                      the same thing in language anyone can read. */}
                  {debug.error && (
                    <div className="mt-1 text-[10px] text-red-500 font-mono">
                      stream error: {debug.error}
                    </div>
                  )}
                  {liveLog.length > 0 && (
                    <details className="mt-2 border-t border-gray-100 pt-2" open>
                      <summary className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden mb-1.5">
                        <AutoAwesomeIcon sx={{ fontSize: 13, color: assistantPrimary }} />
                        Analysis steps
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#f0edff", color: assistantPrimary }}>
                          {liveLog.length}
                        </span>
                        <ExpandMoreIcon sx={{ fontSize: 14, color: "#9ca3af", marginLeft: "auto" }} className="transition-transform duration-200 group-open:rotate-180" />
                      </summary>
                      <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                        {liveLog.map((r) => (
                          <div
                            key={r.key}
                            className="flex items-center gap-1.5 text-[11px] text-gray-500"
                          >
                            {r.status === "running" ? (
                              <CircularProgress size={10} sx={{ color: assistantPrimary }} />
                            ) : r.status === "failed" ? (
                              <ErrorOutlineIcon sx={{ fontSize: 13, color: "#ef4444" }} />
                            ) : (
                              <CheckCircleIcon sx={{ fontSize: 13, color: "#22c55e" }} />
                            )}
                            <span className={r.status === "running" ? "text-gray-700" : ""}>
                              {r.label}
                            </span>
                            {r.detail && (
                              <span className="text-gray-400 truncate">· {r.detail}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* input */}
          <div className="flex-shrink-0 bg-white border-t border-gray-100 px-3 pt-2.5 pb-2">
          <div
            style={{
              background: uiConfig.aiAssistantInputBackground || "#f8fafc",
              border: `1.5px solid ${inputFocused ? assistantPrimary : "#e5e7eb"}`,
              borderRadius: 14,
              boxShadow: inputFocused
                ? `0 0 0 4px color-mix(in srgb, ${assistantPrimary} 12%, transparent)`
                : "none",
            }}
          >
            <div className="flex items-start gap-2" style={{ padding: "10px 12px 4px" }}>
              <AutoAwesomeIcon
                sx={{ fontSize: 16, color: inputFocused ? assistantPrimary : "#94a3b8", mt: "2px", flexShrink: 0 }}
              />
              <textarea
                rows={1}
                className="flex-1 text-sm bg-transparent resize-none focus:outline-none"
                style={{ minHeight: 45, maxHeight: 100, overflowY: "auto" }}
                placeholder={dataMode === "onboarding" ? "Ask using Text-to-DAX…" : dataMode === "source" ? "Ask about the live source dataset…" : "Ask about the data…"}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                disabled={loading}
              />
            </div>

            {/* Bottom toolbar — agent (data mode) picker, model badge, send —
                mirrors a chat composer's own input bar layout. */}
            <div className="flex items-center gap-2 px-2.5 pb-2 pt-1">
              <select
                value={dataMode}
                onChange={(e) => setDataMode(e.target.value)}
                disabled={loading}
                title={
                  dataMode === "onboarding"
                    ? "Answer using AI-onboarded grounding (schema, measures, KPIs, glossary)."
                    : dataMode === "source"
                    ? "Query the report's live Power BI dataset for this question."
                    : "Answer from the visuals already exported from this report view."
                }
                className="text-[11px] font-semibold rounded-lg border border-gray-200 bg-white px-2 py-1 focus:outline-none disabled:opacity-60"
                style={{ color: assistantPrimary }}
              >
 
                {onboardingStatus?.enabled && <option value="onboarding">Text-to-DAX</option>}
              </select>

              <span
                className="text-[11px] font-semibold rounded-lg border border-gray-200 bg-white px-2 py-1 text-gray-500"
                title="Model used to answer this chat"
              >
              </span>

              <div className="flex-1" />

              <button
                // Wrapped rather than passed directly: onClick hands the handler
                // a MouseEvent, which sendMessage would read as an "ask again"
                // descriptor — truthy, so it would skip clearing the input and
                // skip echoing the user's question.
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="flex items-center justify-center flex-shrink-0 transition-all hover:scale-105 active:scale-95 disabled:hover:scale-100"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  border: "none",
                  cursor: loading || !input.trim() ? "not-allowed" : "pointer",
                  background: loading || !input.trim() ? "#e2e8f0" : assistantGradient,
                  boxShadow:
                    loading || !input.trim() ? "none" : "0 4px 14px rgba(37,57,120,0.35)",
                }}
              >
                <SendIcon sx={{ fontSize: 14, color: loading || !input.trim() ? "#94a3b8" : "white" }} />
              </button>
            </div>
          </div>
          </div>
          </>
          )}
        </div>
      </div>
  );
}
